import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { supabase } from "../supabaseClient";

const GOLD     = "#C9A96E";
const INK      = "#1C1915";
const INK_SOFT = "#4A4540";
const BORDER   = "#E7E1D8";
const SOFT_BG  = "#F7F5F2";
const WHITE    = "#FFFFFF";
const CRITIQUE = "#C0392B";

const COLORS = ["#C9A96E","#2E6DB4","#1A7A44","#8E44AD","#C0392B","#16A085","#D35400","#2C3E50"];

const inputStyle = {
  width: "100%", padding: "7px 10px",
  border: `1px solid ${BORDER}`, borderRadius: 6,
  fontSize: 12, color: INK, background: "#FAFAF8",
  boxSizing: "border-box", outline: "none",
};
const labelStyle = {
  fontSize: 10, color: INK_SOFT, textTransform: "uppercase",
  letterSpacing: "0.1em", fontWeight: 600, display: "block", marginBottom: 4,
};

const TACHES_PREDEFINIES = [
  { phase: "Prospection & Foncier", couleur: "#7B68EE", taches: ["Étude de faisabilité","Analyse topographique du terrain","Vérification foncière & titre","Signature du compromis de terrain","Acquisition du foncier"] },
  { phase: "Conception & Permis", couleur: "#2E6DB4", taches: ["Élaboration des plans architecturaux","Étude structure & béton armé","Étude VRD & réseaux","Dépôt du permis de construire","Obtention du permis de construire","Consultation bureau de contrôle"] },
  { phase: "Préparation du Chantier", couleur: "#E67E22", taches: ["Appels d'offres entreprises","Sélection et négociation entreprises","Signature des marchés de travaux","Installation de chantier","Déclaration d'ouverture de chantier"] },
  { phase: "Gros Œuvre", couleur: "#C0392B", taches: ["Terrassement & fondations","Dallage & sous-sol","Structure RDC","Structure R+1 (si applicable)","Toiture & charpente","Étanchéité"] },
  { phase: "Second Œuvre", couleur: "#D35400", taches: ["Maçonnerie & cloisons","Plomberie brute","Électricité brute","Menuiseries extérieures (aluminium)","Isolation thermique & acoustique"] },
  { phase: "Finitions", couleur: "#1A7A44", taches: ["Carrelage & revêtements sols","Enduits & peinture intérieure","Menuiseries intérieures (bois)","Plomberie finition & sanitaires","Électricité finition & appareillage","Façade & peinture extérieure","Aménagements extérieurs & piscine","Cuisine & placards sur mesure"] },
  { phase: "Réception & Livraison", couleur: GOLD, taches: ["Pré-réception interne","Levée des réserves","Réception contradictoire","PV de réception signé","Remise des clés","Clôture documentaire"] },
  { phase: "SAV / Parfait Achèvement", couleur: "#95A5A6", taches: ["Suivi garanties décennales","Gestion des réserves post-livraison","Rapport final de clôture"] },
];

// Ordre des phases pour le chemin critique
const PHASE_ORDER = TACHES_PREDEFINIES.map(p => p.phase);

function addDays(date, days) { const d = new Date(date); d.setDate(d.getDate() + days); return d; }
function diffDays(a, b) { return Math.round((new Date(b) - new Date(a)) / 86400000); }
function fmtDate(d) { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); }
function fmtDateInput(d) { return new Date(d).toISOString().split("T")[0]; }

const DAY_W = 28;

function initSelection() {
  const sel = {};
  TACHES_PREDEFINIES.forEach(p => p.taches.forEach(t => { sel[p.phase + "|" + t] = true; }));
  return sel;
}

// ── Calcul chemin critique ────────────────────────────────────────────────
// Algorithme :
// 1. Trouver la date de fin globale du projet
// 2. Par phase (dans l'ordre logique), identifier la tâche qui finit le plus tard
// 3. Ces tâches forment le chemin critique
function calculerCheminCritique(taches) {
  if (!taches.length) return new Set();

  // Date de fin globale
  const finGlobale = Math.max(...taches.map(t => new Date(t.date_fin).getTime()));

  // Grouper par couleur (proxy pour la phase)
  const parPhase = {};
  taches.forEach(t => {
    const key = t.couleur || "autre";
    if (!parPhase[key]) parPhase[key] = [];
    parPhase[key].push(t);
  });

  // Pour chaque groupe de couleur, trouver la tâche qui finit le plus tard
  // + toute tâche dont la date_fin === finGlobale
  const critique = new Set();

  Object.values(parPhase).forEach(groupe => {
    const maxFin = Math.max(...groupe.map(t => new Date(t.date_fin).getTime()));
    // La tâche la plus tardive de chaque phase
    groupe.filter(t => new Date(t.date_fin).getTime() === maxFin).forEach(t => critique.add(t.id));
  });

  // Toujours inclure les tâches qui finissent à la date globale
  taches.filter(t => new Date(t.date_fin).getTime() === finGlobale).forEach(t => critique.add(t.id));

  // Inclure aussi les tâches dont le début = fin d'une tâche critique (liens)
  // (2 passes pour propager)
  for (let pass = 0; pass < 2; pass++) {
    const critiqueDates = new Set(
      taches.filter(t => critique.has(t.id)).map(t => t.date_debut)
    );
    taches.forEach(t => {
      if (critiqueDates.has(t.date_fin) || critiqueDates.has(fmtDateInput(addDays(new Date(t.date_fin), 1)))) {
        critique.add(t.id);
      }
    });
  }

  return critique;
}

export default function GanttPage({ isOPCMandate = false, cabinetId = null } = {}) {
  void isOPCMandate;
  const [projets,       setProjets]       = useState([]);
  const [projetSelec,   setProjetSelec]   = useState("tous");
  const [taches,        setTaches]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showForm,      setShowForm]      = useState(false);
  const [editTache,     setEditTache]     = useState(null);
  const [msg,           setMsg]           = useState("");
  const [viewMode,      setViewMode]      = useState("semaine");

  // Chemin critique
  const [showCritique,  setShowCritique]  = useState(false);

  // IA
  const [genLoading,    setGenLoading]    = useState(false);
  const [genApercu,     setGenApercu]     = useState(null);
  const [showApercu,    setShowApercu]    = useState(false);
  const [typeProjet,    setTypeProjet]    = useState("villa");
  const [dateDebut,     setDateDebut]     = useState(fmtDateInput(new Date()));

  // Sélection tâches
  const [showSelection,  setShowSelection]  = useState(false);
  const [tacheSelection, setTacheSelection] = useState(initSelection);

  const ganttRef = useRef(null);
  const today = new Date(); today.setHours(0,0,0,0);

  const [form, setForm] = useState({
    nom: "", projet_id: "", date_debut: "", date_fin: "",
    avancement: 0, couleur: COLORS[0], source: "manuel",
  });

  useEffect(() => { if (cabinetId == null) return; fetchAll(); }, [cabinetId]);

  async function fetchAll() {
    setLoading(true);
    const [{ data: projs }, { data: jalons }] = await Promise.all([
      supabase.from("projects").select("id, name").eq("cabinet_id", cabinetId).order("name"),
      supabase.from("milestones").select("*").eq("cabinet_id", cabinetId).order("due_date"),
    ]);
    if (projs) setProjets(projs);
    const tachesJalons = (jalons || []).filter(j => j.due_date).map((j, i) => ({
      id: "jalon_" + j.id,
      nom: j.title || j.name || "Jalon",
      projet_id: j.project_id,
      date_debut: j.start_date || fmtDateInput(addDays(new Date(j.due_date), -7)),
      date_fin: j.due_date,
      avancement: j.progress || 0,
      couleur: COLORS[i % COLORS.length],
      source: "jalon",
    }));
    const saved = JSON.parse(localStorage.getItem("yoma_gantt_taches") || "[]");
    setTaches([...tachesJalons, ...saved]);
    setLoading(false);
  }

  function saveTachesLocales(liste) {
    localStorage.setItem("yoma_gantt_taches", JSON.stringify(liste.filter(t => t.source === "manuel")));
  }

  function toggleTache(phase, tache) {
    const key = phase + "|" + tache;
    setTacheSelection(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function togglePhase(phase, taches) {
    const keys = taches.map(t => phase + "|" + t);
    const allChecked = keys.every(k => tacheSelection[k]);
    setTacheSelection(prev => {
      const next = { ...prev };
      keys.forEach(k => { next[k] = !allChecked; });
      return next;
    });
  }

  function getTachesSelectionnees() {
    const result = [];
    TACHES_PREDEFINIES.forEach(p => {
      p.taches.forEach(t => {
        if (tacheSelection[p.phase + "|" + t]) result.push({ nom: t, phase: p.phase, couleur: p.couleur });
      });
    });
    return result;
  }

  const nbSelectionnees = getTachesSelectionnees().length;

  async function genererPlanningIA() {
    setGenLoading(true); setMsg(""); setShowSelection(false);
    const projet = projets.find(p => p.id === projetSelec);
    const tachesChoisies = getTachesSelectionnees();
    const prompt = `Tu es expert en coordination de projets immobiliers au Maroc (OPC).
Génère un planning Gantt pour un projet de type "${typeProjet}"${projet ? ` nommé "${projet.name}"` : ""}.
Date de démarrage : ${dateDebut}.

L'utilisateur a sélectionné EXACTEMENT ces tâches à planifier (respecte-les toutes, sans en ajouter ni en supprimer) :
${tachesChoisies.map(t => `- [${t.phase}] ${t.nom}`).join("\n")}

Règles :
- Respecte la logique de construction et l'enchaînement logique des tâches
- Attribue des durées réalistes selon le type de projet
- Les tâches d'une même phase peuvent se chevaucher légèrement
- Commence à partir de ${dateDebut}

Réponds UNIQUEMENT en JSON valide, sans texte avant ni après, sans backticks :
[{"nom":"...","date_debut":"YYYY-MM-DD","date_fin":"YYYY-MM-DD","avancement":0,"couleur":"#HEX","description":"..."}]

Couleurs : Prospection #7B68EE, Conception #2E6DB4, Préparation #E67E22, Gros Œuvre #C0392B, Second Œuvre #D35400, Finitions #1A7A44, Réception #C9A96E, SAV #95A5A6`;

try {
    const { data, error } = await supabase.functions.invoke("generate-gantt", { body: { prompt } });
    if (error) throw new Error("Edge error: " + error.message);
    const text = (data.content || []).map(c => c.text || "").join("");
    
    // Extraction robuste du JSON : cherche le premier [ et le dernier ]
    const startIdx = text.indexOf("[");
    const endIdx   = text.lastIndexOf("]");
    if (startIdx === -1 || endIdx === -1) throw new Error("Pas de JSON trouvé dans la réponse");
    const clean = text.slice(startIdx, endIdx + 1);
    
    let parsed;
    try {
      parsed = JSON.parse(clean);
    } catch (parseErr) {
      // Tentative de réparation : couper à la dernière virgule + fermer le tableau
      const lastBrace = clean.lastIndexOf("}");
      const repaired  = clean.slice(0, lastBrace + 1) + "]";
      parsed = JSON.parse(repaired);
    }

    const tachesIA = parsed.map((t, i) => ({
      ...t, id: "ia_" + Date.now() + "_" + i,
      projet_id: projetSelec !== "tous" ? projetSelec : "",
      source: "manuel",
    }));
    setGenApercu(tachesIA); setShowApercu(true);
} catch (err) { 
    setMsg("Erreur génération IA : " + err.message); 
    console.error(err); 
  }
  setGenLoading(false);
  }

  function confirmerPlanning() {
    const updated = [...taches, ...genApercu];
    setTaches(updated); saveTachesLocales(updated);
    setShowApercu(false); setGenApercu(null);
    setMsg("Planning généré et ajouté au Gantt ✓");
    setTimeout(() => setMsg(""), 3000);
  }

  const tachesFiltrees = projetSelec === "tous" ? taches : taches.filter(t => t.projet_id === projetSelec);
  const sourceDates = showApercu && genApercu ? [...tachesFiltrees, ...genApercu] : tachesFiltrees;
  const dates = sourceDates.flatMap(t => [new Date(t.date_debut), new Date(t.date_fin)]);
  const minDate = dates.length ? new Date(Math.min(...dates)) : today;
  const maxDate = dates.length ? new Date(Math.max(...dates)) : addDays(today, 60);
  minDate.setDate(minDate.getDate() - 7);
  maxDate.setDate(maxDate.getDate() + 14);
  const totalDays = diffDays(minDate, maxDate);

  // ── Chemin critique calculé ───────────────────────────────────────────────
  const cheminCritique = useMemo(() => {
    if (!showCritique) return new Set();
    return calculerCheminCritique(tachesFiltrees);
  }, [showCritique, tachesFiltrees]);

  // Stats chemin critique
  const tachesCritiques = tachesFiltrees.filter(t => cheminCritique.has(t.id));
  const dureeCritique = tachesCritiques.length > 0
    ? diffDays(
        new Date(Math.min(...tachesCritiques.map(t => new Date(t.date_debut).getTime()))),
        new Date(Math.max(...tachesCritiques.map(t => new Date(t.date_fin).getTime())))
      )
    : 0;

  function genererHeaders() {
    const headers = []; let cur = new Date(minDate);
    while (cur < maxDate) {
      if (viewMode === "semaine") {
        const label = `S${Math.ceil(cur.getDate() / 7)} ${cur.toLocaleDateString("fr-FR", { month: "short" })}`;
        headers.push({ label, date: new Date(cur), days: 7 }); cur = addDays(cur, 7);
      } else {
        const label = cur.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
        const daysInMonth = new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getDate();
        headers.push({ label, date: new Date(cur), days: daysInMonth });
        cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      }
    }
    return headers;
  }
  const headers = genererHeaders();

  function getBarStyle(tache) {
    const start = Math.max(0, diffDays(minDate, new Date(tache.date_debut)));
    const end   = Math.min(totalDays, diffDays(minDate, new Date(tache.date_fin)));
    return { left: start * DAY_W, width: Math.max(DAY_W, (end - start) * DAY_W) };
  }

  const dragging = useRef(null);
  function onBarMouseDown(e, tache) {
    if (tache.source === "jalon") return;
    e.preventDefault();
    dragging.current = { id: tache.id, startX: e.clientX, origDebut: new Date(tache.date_debut), origFin: new Date(tache.date_fin) };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }
  const onMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    const deltaDays = Math.round((e.clientX - dragging.current.startX) / DAY_W);
    if (deltaDays === 0) return;
    setTaches(prev => prev.map(t => t.id !== dragging.current.id ? t : {
      ...t,
      date_debut: fmtDateInput(addDays(dragging.current.origDebut, deltaDays)),
      date_fin:   fmtDateInput(addDays(dragging.current.origFin,   deltaDays)),
    }));
  }, []);
  const onMouseUp = useCallback(() => {
    if (dragging.current) { setTaches(prev => { saveTachesLocales(prev); return prev; }); dragging.current = null; }
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  }, [onMouseMove]);

  function handleSauvegarder() {
    if (!form.nom || !form.date_debut || !form.date_fin) { setMsg("Nom, date début et fin requis."); return; }
    const updated = editTache
      ? taches.map(t => t.id === editTache.id ? { ...t, ...form } : t)
      : [...taches, { ...form, id: "manuel_" + Date.now(), source: "manuel" }];
    setTaches(updated); saveTachesLocales(updated);
    setShowForm(false); setEditTache(null);
    setForm({ nom: "", projet_id: "", date_debut: "", date_fin: "", avancement: 0, couleur: COLORS[0], source: "manuel" });
    setMsg("Tâche sauvegardée ✓"); setTimeout(() => setMsg(""), 2000);
  }
  function handleSupprimer(id) { const u = taches.filter(t => t.id !== id); setTaches(u); saveTachesLocales(u); }
  function handleEditer(t) {
    setEditTache(t);
    setForm({ nom: t.nom, projet_id: t.projet_id || "", date_debut: t.date_debut, date_fin: t.date_fin, avancement: t.avancement, couleur: t.couleur, source: t.source });
    setShowForm(true);
  }

  const todayLeft = diffDays(minDate, today) * DAY_W;
  const projetNom = (id) => projets.find(p => p.id === id)?.name || "";
  const tachesAffichees = showApercu && genApercu
    ? [...tachesFiltrees, ...genApercu.map(t => ({ ...t, _apercu: true }))]
    : tachesFiltrees;

  if (loading) return <div style={{ padding: 48, color: "#bbb", textAlign: "center" }}>Chargement...</div>;

  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: "#8B7361" }}>Chargement...</div>;
  }

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", color: INK }}><style>{`
        @media print {
          body * { visibility: hidden !important; }
          #gantt-print, #gantt-print * { visibility: visible !important; }
          #gantt-print { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
          @page { margin: 15mm; size: A3 landscape; }
        }
        .gantt-bar-critique {
          box-shadow: 0 0 0 2px #C0392B, 0 0 12px rgba(192,57,43,0.4) !important;
          animation: pulse-critique 2s ease-in-out infinite;
        }
        @keyframes pulse-critique {
          0%, 100% { box-shadow: 0 0 0 2px #C0392B, 0 0 8px rgba(192,57,43,0.3); }
          50%       { box-shadow: 0 0 0 2px #C0392B, 0 0 18px rgba(192,57,43,0.6); }
        }
      `}</style>

      {/* En-tête */}
      <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}><div><div style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: GOLD, marginBottom: 4 }}>Outils — Planning</div><div style={{ fontSize: 24, fontWeight: 700 }}>Planning Gantt</div><div style={{ fontSize: 13, color: INK_SOFT, marginTop: 3 }}>Jalons + IA · Glisser-déposer · Chemin critique · Export PDF</div></div><div className="no-print" style={{ display: "flex", gap: 10 }}><button onClick={() => window.print()}
            style={{ padding: "8px 18px", background: SOFT_BG, color: INK_SOFT, border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
             Imprimer
          </button><button onClick={() => { setShowForm(true); setEditTache(null); setForm({ nom: "", projet_id: "", date_debut: "", date_fin: "", avancement: 0, couleur: COLORS[0], source: "manuel" }); }}
            style={{ padding: "8px 18px", background: SOFT_BG, color: INK, border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            + Tâche manuelle
          </button></div></div>

      {msg && <div style={{ marginBottom: 14, padding: "10px 16px", background: "#E8F6EF", borderRadius: 8, fontSize: 13, color: "#1A7A44", fontWeight: 600 }}>{msg}</div>}

      {/* ── Panneau Génération IA ── */}
      <div className="no-print" style={{ background: WHITE, border: `2px solid ${GOLD}`, borderRadius: 12, padding: "18px 22px", marginBottom: 16 }}><div style={{ fontSize: 12, fontWeight: 700, color: GOLD, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 14 }}>
           Générer le planning automatiquement avec l'IA
        </div><div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, marginBottom: 14 }}><div><label style={labelStyle}>Projet</label><select style={inputStyle} value={projetSelec} onChange={e => setProjetSelec(e.target.value)}><option value="tous">— Sélectionner un projet —</option>
              {projets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></div><div><label style={labelStyle}>Type de projet</label><select style={inputStyle} value={typeProjet} onChange={e => setTypeProjet(e.target.value)}><option value="villa">Villa individuelle</option><option value="lotissement">Lotissement</option><option value="renovation">Rénovation</option><option value="riad">Riad</option><option value="immeuble">Immeuble</option></select></div><div><label style={labelStyle}>Date de démarrage</label><input style={inputStyle} type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} /></div></div><div style={{ display: "flex", alignItems: "center", gap: 12 }}><button onClick={() => setShowSelection(v => !v)}
            style={{ padding: "9px 20px", background: showSelection ? INK : SOFT_BG, color: showSelection ? WHITE : INK, border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            {showSelection ? "▲ Masquer la sélection" : "▼ Sélectionner les tâches"}
          </button><span style={{ fontSize: 12, color: INK_SOFT }}><strong style={{ color: GOLD }}>{nbSelectionnees}</strong> tâche{nbSelectionnees > 1 ? "s" : ""} sélectionnée{nbSelectionnees > 1 ? "s" : ""}
          </span><button onClick={genererPlanningIA} disabled={genLoading || nbSelectionnees === 0}
            style={{ marginLeft: "auto", padding: "9px 22px", background: GOLD, color: WHITE, border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: genLoading ? "wait" : "pointer", whiteSpace: "nowrap", opacity: (genLoading || nbSelectionnees === 0) ? 0.6 : 1 }}>
            {genLoading ? " Génération..." : " Générer le planning"}
          </button></div>

        {showSelection && (
          <div style={{ marginTop: 16, borderTop: `1px solid ${BORDER}`, paddingTop: 16 }}><div style={{ fontSize: 11, color: INK_SOFT, marginBottom: 12, fontStyle: "italic" }}>Cochez les tâches à inclure dans le planning généré par l'IA</div><div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
              {TACHES_PREDEFINIES.map(phase => {
                const allChecked = phase.taches.every(t => tacheSelection[phase.phase + "|" + t]);
                const someChecked = phase.taches.some(t => tacheSelection[phase.phase + "|" + t]);
                return (
                  <div key={phase.phase} style={{ background: SOFT_BG, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}><div onClick={() => togglePhase(phase.phase, phase.taches)}
                      style={{ padding: "10px 14px", background: phase.couleur, display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}><div style={{ width: 16, height: 16, borderRadius: 4, border: "2px solid rgba(255,255,255,0.8)", background: allChecked ? "rgba(255,255,255,0.9)" : someChecked ? "rgba(255,255,255,0.4)" : "transparent", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {allChecked && <span style={{ color: phase.couleur, fontSize: 11, fontWeight: 900 }}>✓</span>}
                        {someChecked && !allChecked && <span style={{ color: "white", fontSize: 11 }}>−</span>}
                      </div><span style={{ fontSize: 11, fontWeight: 800, color: WHITE, letterSpacing: "0.08em", textTransform: "uppercase" }}>{phase.phase}</span><span style={{ marginLeft: "auto", fontSize: 10, color: "rgba(255,255,255,0.8)" }}>
                        {phase.taches.filter(t => tacheSelection[phase.phase + "|" + t]).length}/{phase.taches.length}
                      </span></div><div style={{ padding: "8px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
                      {phase.taches.map(tache => {
                        const key = phase.phase + "|" + tache;
                        const checked = !!tacheSelection[key];
                        return (
                          <label key={tache} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "3px 0" }}><input type="checkbox" checked={checked} onChange={() => toggleTache(phase.phase, tache)} style={{ accentColor: phase.couleur, width: 14, height: 14, flexShrink: 0 }} /><span style={{ fontSize: 12, color: checked ? INK : "#bbb", textDecoration: checked ? "none" : "line-through", transition: "all 0.15s" }}>{tache}</span></label>
                        );
                      })}
                    </div></div>
                );
              })}
            </div><div style={{ marginTop: 14, display: "flex", gap: 10 }}><button onClick={() => setTacheSelection(initSelection())} style={{ padding: "7px 16px", background: SOFT_BG, color: INK_SOFT, border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 11, cursor: "pointer" }}>✓ Tout sélectionner</button><button onClick={() => setTacheSelection({})} style={{ padding: "7px 16px", background: SOFT_BG, color: INK_SOFT, border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 11, cursor: "pointer" }}>✗ Tout décocher</button></div></div>
        )}
      </div>

      {/* ── Aperçu IA ── */}
      {showApercu && genApercu && (
        <div className="no-print" style={{ background: "#FFFDF5", border: `1px solid ${GOLD}`, borderRadius: 12, padding: "18px 22px", marginBottom: 16 }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}><div><div style={{ fontSize: 13, fontWeight: 700, color: INK, marginBottom: 2 }}>✓ {genApercu.length} tâches générées par l'IA</div><div style={{ fontSize: 12, color: INK_SOFT }}>Vérifiez les dates avant de confirmer</div></div><div style={{ display: "flex", gap: 10 }}><button onClick={() => { setShowApercu(false); setGenApercu(null); }} style={{ padding: "8px 18px", background: SOFT_BG, color: INK_SOFT, border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 12, cursor: "pointer" }}>Annuler</button><button onClick={confirmerPlanning} style={{ padding: "8px 22px", background: "#1A7A44", color: WHITE, border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>✓ Confirmer et ajouter au Gantt</button></div></div><div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 }}>
            {genApercu.map((t, i) => (
              <div key={i} style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 14px", display: "flex", gap: 10, alignItems: "center" }}><div style={{ width: 10, height: 10, borderRadius: "50%", background: t.couleur, flexShrink: 0 }} /><div style={{ flex: 1 }}><div style={{ fontSize: 12, fontWeight: 700 }}>{t.nom}</div><div style={{ fontSize: 11, color: INK_SOFT }}>{fmtDate(t.date_debut)} → {fmtDate(t.date_fin)}</div>
                  {t.description && <div style={{ fontSize: 10, color: INK_SOFT, marginTop: 2, fontStyle: "italic" }}>{t.description}</div>}
                </div></div>
            ))}
          </div></div>
      )}

      {/* Filtres + Chemin critique */}
      <div className="no-print" style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "12px 18px", marginBottom: 16, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}><div style={{ display: "flex", gap: 6, alignItems: "center" }}><span style={{ fontSize: 11, color: INK_SOFT, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>Afficher</span><select style={{ ...inputStyle, width: "auto" }} value={projetSelec} onChange={e => setProjetSelec(e.target.value)}><option value="tous">Tous les projets</option>
            {projets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></div><div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {["semaine", "mois"].map(v => (
            <button key={v} onClick={() => setViewMode(v)}
              style={{ padding: "5px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer", fontWeight: viewMode === v ? 700 : 400, background: viewMode === v ? INK : SOFT_BG, color: viewMode === v ? WHITE : INK_SOFT, border: `1px solid ${viewMode === v ? INK : BORDER}` }}>
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        {/* ── BOUTON CHEMIN CRITIQUE ── */}
        <button
          onClick={() => setShowCritique(v => !v)}
          style={{
            padding: "7px 16px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
            background: showCritique ? CRITIQUE : SOFT_BG,
            color: showCritique ? WHITE : INK_SOFT,
            border: `2px solid ${showCritique ? CRITIQUE : BORDER}`,
            transition: "all 0.2s",
            display: "flex", alignItems: "center", gap: 6,
          }}>
           Chemin critique
          {showCritique && tachesCritiques.length > 0 && (
            <span style={{ background: "rgba(255,255,255,0.2)", borderRadius: 4, padding: "1px 6px", fontSize: 10 }}>
              {tachesCritiques.length} tâches · {dureeCritique}j
            </span>
          )}
        </button><div style={{ marginLeft: "auto", fontSize: 11, color: INK_SOFT, display: "flex", gap: 12 }}><span> Manuel</span><span> Jalon</span><span> IA</span>
          {showCritique && <span style={{ color: CRITIQUE, fontWeight: 700 }}> Chemin critique</span>}
        </div></div>

      {/* ── Bandeau chemin critique info ── */}
      {showCritique && tachesCritiques.length > 0 && (
        <div style={{ background: "#FDF0EE", border: `1px solid ${CRITIQUE}44`, borderRadius: 10, padding: "12px 18px", marginBottom: 16, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}><div style={{ width: 10, height: 10, borderRadius: "50%", background: CRITIQUE, flexShrink: 0 }} /><div><span style={{ fontSize: 12, fontWeight: 700, color: CRITIQUE }}>Chemin critique identifié</span><span style={{ fontSize: 12, color: INK_SOFT, marginLeft: 10 }}>
              {tachesCritiques.length} tâche{tachesCritiques.length > 1 ? "s" : ""} · durée totale <strong style={{ color: CRITIQUE }}>{dureeCritique} jours</strong></span></div><div style={{ marginLeft: "auto", fontSize: 11, color: INK_SOFT, fontStyle: "italic" }}>
             Tout retard sur ces tâches décale la livraison du projet
          </div></div>
      )}

      {/* Formulaire tâche manuelle */}
      {showForm && (
        <div className="no-print" style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "20px 24px", marginBottom: 16 }}><div style={{ fontSize: 12, fontWeight: 700, color: GOLD, marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.1em" }}>
            {editTache ? "Modifier la tâche" : "Nouvelle tâche"}
          </div><div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}><div><label style={labelStyle}>Nom</label><input style={inputStyle} value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} placeholder="Ex : Fondations" /></div><div><label style={labelStyle}>Date début</label><input style={inputStyle} type="date" value={form.date_debut} onChange={e => setForm(f => ({ ...f, date_debut: e.target.value }))} /></div><div><label style={labelStyle}>Date fin</label><input style={inputStyle} type="date" value={form.date_fin} onChange={e => setForm(f => ({ ...f, date_fin: e.target.value }))} /></div><div><label style={labelStyle}>Projet</label><select style={inputStyle} value={form.projet_id} onChange={e => setForm(f => ({ ...f, projet_id: e.target.value }))}><option value="">— Aucun —</option>
                {projets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></div></div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}><div><label style={labelStyle}>Avancement : <strong style={{ color: GOLD }}>{form.avancement}%</strong></label><input type="range" min="0" max="100" step="5" value={form.avancement} onChange={e => setForm(f => ({ ...f, avancement: parseInt(e.target.value) }))} style={{ width: "100%", accentColor: GOLD }} /></div><div><label style={labelStyle}>Couleur</label><div style={{ display: "flex", gap: 6 }}>
                {COLORS.map(c => <div key={c} onClick={() => setForm(f => ({ ...f, couleur: c }))} style={{ width: 24, height: 24, borderRadius: "50%", background: c, cursor: "pointer", border: form.couleur === c ? "3px solid " + INK : "2px solid transparent" }} />)}
              </div></div></div><div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}><button onClick={() => { setShowForm(false); setEditTache(null); }} style={{ padding: "8px 18px", background: SOFT_BG, color: INK_SOFT, border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 12, cursor: "pointer" }}>Annuler</button><button onClick={handleSauvegarder} style={{ padding: "8px 18px", background: GOLD, color: WHITE, border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{editTache ? "Modifier" : "Ajouter"}</button></div></div>
      )}

      {/* ── GANTT ── */}
      <div id="gantt-print" style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, overflow: "hidden" }}>
        {tachesAffichees.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center", color: "#bbb", fontStyle: "italic", fontSize: 13 }}>
            Aucune tâche — utilisez "Générer le planning" ou ajoutez une tâche manuellement
          </div>
        ) : (
          <div style={{ display: "flex" }}>
            {/* Colonne noms */}
            <div style={{ width: 260, flexShrink: 0, borderRight: `1px solid ${BORDER}` }}><div style={{ height: 48, borderBottom: `2px solid ${BORDER}`, padding: "0 16px", display: "flex", alignItems: "center", background: SOFT_BG }}><span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_SOFT }}>Tâche</span></div>
              {tachesAffichees.map((t, i) => {
                const isCritique = showCritique && cheminCritique.has(t.id);
                const isDimmed   = showCritique && !cheminCritique.has(t.id) && !t._apercu;
                return (
                  <div key={t.id} style={{
                    height: 48, borderBottom: `1px solid ${SOFT_BG}`, padding: "0 12px",
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                    background: isCritique ? "#FDF0EE" : t._apercu ? "#FFFDF0" : i % 2 === 0 ? WHITE : "#FDFCFB",
                    opacity: isDimmed ? 0.35 : 1,
                    transition: "opacity 0.3s",
                    borderLeft: isCritique ? `3px solid ${CRITIQUE}` : "3px solid transparent",
                  }}><div style={{ flex: 1, overflow: "hidden" }}><div style={{ fontSize: 12, fontWeight: isCritique ? 800 : 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: isCritique ? CRITIQUE : INK }}>
                        {isCritique && <span style={{ marginRight: 4 }}></span>}
                        {t._apercu ? " " : ""}{t.nom}
                      </div>
                      {t.projet_id && <div style={{ fontSize: 10, color: INK_SOFT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{projetNom(t.projet_id)}</div>}
                    </div>
                    {t.source === "manuel" && !t._apercu && (
                      <div className="no-print" style={{ display: "flex", gap: 4, flexShrink: 0 }}><button onClick={() => handleEditer(t)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: INK_SOFT, padding: "2px 4px" }}>Mod.</button><button onClick={() => handleSupprimer(t.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#C0392B", padding: "2px 4px" }}>×</button></div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Zone Gantt */}
            <div ref={ganttRef} style={{ flex: 1, overflowX: "auto", position: "relative" }}><div style={{ width: totalDays * DAY_W, minWidth: "100%" }}>
                {/* Headers */}
                <div style={{ height: 48, borderBottom: `2px solid ${BORDER}`, display: "flex", background: SOFT_BG }}>
                  {headers.map((h, i) => (
                    <div key={i} style={{ width: h.days * DAY_W, flexShrink: 0, borderRight: `1px solid ${BORDER}`, display: "flex", alignItems: "center", padding: "0 8px" }}><span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: INK_SOFT, whiteSpace: "nowrap" }}>{h.label}</span></div>
                  ))}
                </div>

                {/* Barres */}
                <div style={{ position: "relative" }}>
                  {tachesAffichees.map((t, i) => {
                    const bar = getBarStyle(t);
                    const isJalon    = t.source === "jalon";
                    const isCritique = showCritique && cheminCritique.has(t.id);
                    const isDimmed   = showCritique && !cheminCritique.has(t.id) && !t._apercu;
                    return (
                      <div key={t.id} style={{
                        height: 48, borderBottom: `1px solid ${SOFT_BG}`, position: "relative",
                        background: isCritique ? "#FDF0EE" : t._apercu ? "#FFFDF0" : i % 2 === 0 ? WHITE : "#FDFCFB",
                        opacity: isDimmed ? 0.3 : 1,
                        transition: "opacity 0.3s",
                      }}>
                        {headers.map((h, hi) => {
                          const x = diffDays(minDate, h.date) * DAY_W;
                          return <div key={hi} style={{ position: "absolute", left: x, top: 0, bottom: 0, width: 1, background: BORDER, opacity: 0.5 }} />;
                        })}

                        <div
                          onMouseDown={e => onBarMouseDown(e, t)}
                          className={isCritique ? "gantt-bar-critique" : ""}
                          style={{
                            position: "absolute", left: bar.left, width: bar.width,
                            top: 10, height: 28, borderRadius: 6,
                            background: isCritique ? CRITIQUE : t.couleur,
                            cursor: isJalon ? "default" : "grab",
                            opacity: t._apercu ? 0.6 : isJalon ? 0.75 : 1,
                            boxShadow: isCritique ? undefined : "0 2px 6px rgba(0,0,0,0.12)",
                            overflow: "hidden", userSelect: "none",
                            border: t._apercu ? "2px dashed rgba(255,255,255,0.6)" : "none",
                            transition: "background 0.3s",
                          }}><div style={{ height: "100%", width: `${t.avancement}%`, background: "rgba(255,255,255,0.3)", borderRadius: 6 }} /><div style={{ position: "absolute", top: 0, left: 8, right: 8, height: "100%", display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontSize: 11, fontWeight: 700, color: WHITE, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {isCritique ? " " : ""}{t.nom}
                            </span><span style={{ fontSize: 10, color: "rgba(255,255,255,0.85)", flexShrink: 0 }}>{t.avancement}%</span></div></div></div>
                    );
                  })}

                  {/* Ligne aujourd'hui */}
                  {todayLeft >= 0 && todayLeft <= totalDays * DAY_W && (
                    <div style={{ position: "absolute", left: todayLeft, top: 0, bottom: 0, width: 2, background: "#C0392B", opacity: 0.7, zIndex: 10, pointerEvents: "none" }}><div style={{ position: "absolute", top: 0, left: -18, background: "#C0392B", color: WHITE, fontSize: 9, fontWeight: 700, padding: "2px 4px", borderRadius: 3, whiteSpace: "nowrap" }}>Auj.</div></div>
                  )}
                </div></div></div></div>
        )}
      </div>

      {tachesAffichees.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", gap: 20, fontSize: 11, color: INK_SOFT, flexWrap: "wrap" }}><span> {fmtDate(minDate)} → {fmtDate(maxDate)}</span><span> {tachesAffichees.length} tâche{tachesAffichees.length > 1 ? "s" : ""}</span><span style={{ color: "#C0392B" }}>▌ Aujourd'hui : {fmtDate(today)}</span>
          {showCritique && tachesCritiques.length > 0 && (
            <span style={{ color: CRITIQUE, fontWeight: 700 }}> Chemin critique : {tachesCritiques.length} tâches · {dureeCritique}j</span>
          )}
        </div>
      )}
    </div>
  );
}